import { useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductionDeclarationMaterialAdditionalFilesDialog } from '@/features/production-declaration/components/statement/production-declaration-material-additional-files-dialog';
import { ProductionDeclarationMaterialAdditionalFilesList } from '@/features/production-declaration/components/statement/production-declaration-material-additional-files-list';
import { ProductionDeclarationMaterialCommentsSection } from '@/features/production-declaration/components/statement/production-declaration-material-comments-section';
import { ProductionDeclarationMaterialCommodityOriginCard } from '@/features/production-declaration/components/statement/production-declaration-material-commodity-origin-card';
import { ProductionDeclarationMaterialAdditionalFilesTooltipContent } from '@/features/production-declaration/components/statement/tooltips/production-declaration-material-additional-files-tooltip-content';
import { useCanEditProductionDeclaration } from '@/features/production-declaration/hooks/use-can-edit-production-declaration';
import { useProductionDeclarationCorrectionRequestComment } from '@/features/production-declaration/hooks/use-production-declaration-correction-request-comment';
import { useGetProductionDeclarationMaterialCommodityOriginDocuments } from '@/services/apis/queries/production-declaration/use-get-production-declaration-material-commodity-origin-documents';
import { CorrectionRequestEntityType } from '@/types/core/purchase-order/correction-request';
import type { ProductionDeclarationStatusCode as ProductionDeclarationStatusCodeType } from '@/types/core/purchase-order/production-declaration-status-code';

type ProductionDeclarationMaterialAdditionalFilesCardProps = {
  canComment?: boolean;
  commodityOriginId: string;
  showSupplierCorrectionComments?: boolean;
  statusCode: ProductionDeclarationStatusCodeType;
};

export function ProductionDeclarationMaterialAdditionalFilesCard({
  canComment = false,
  commodityOriginId,
  showSupplierCorrectionComments = false,
  statusCode,
}: ProductionDeclarationMaterialAdditionalFilesCardProps) {
  const { id, materialId } = useParams({ strict: false });
  const { token } = useSearch({ strict: false });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const productionDeclarationId = id ?? '';
  const currentMaterialId = materialId ?? '';
  const { comments, create, edit, remove, resolution } =
    useProductionDeclarationCorrectionRequestComment({
      productionDeclarationId,
      materialId: currentMaterialId,
      commodityOriginId,
      entityType: CorrectionRequestEntityType.CommodityOriginDocuments,
      token,
    });

  const { data: documents = [], isLoading } =
    useGetProductionDeclarationMaterialCommodityOriginDocuments(
      productionDeclarationId,
      currentMaterialId,
      commodityOriginId,
      token,
    );
  const canManageDocuments = useCanEditProductionDeclaration(statusCode);

  if (!(id && materialId)) {
    return null;
  }

  const isInitialLoading = isLoading && documents.length === 0;
  const hasDocuments = documents.length > 0;

  return (
    <>
      <ProductionDeclarationMaterialCommodityOriginCard
        actionDisabled={!canManageDocuments}
        actionLabel={t('materialCommodityOrigins.cards.additionalFiles.cta')}
        description={t(
          hasDocuments
            ? 'materialCommodityOrigins.cards.additionalFiles.description'
            : 'materialCommodityOrigins.cards.additionalFiles.empty',
        )}
        onActionClick={
          canManageDocuments ? () => setIsDialogOpen(true) : undefined
        }
        onCommentClick={canComment ? create.open : undefined}
        title={t('materialCommodityOrigins.cards.additionalFiles.title')}
        tooltipAriaLabel={t(
          'materialCommodityOrigins.cards.additionalFiles.tooltip.title',
        )}
        tooltipContent={
          <ProductionDeclarationMaterialAdditionalFilesTooltipContent />
        }
      >
        <div className='space-y-6'>
          <ProductionDeclarationMaterialCommentsSection
            canComment={canComment}
            comments={comments}
            create={create}
            edit={edit}
            remove={remove}
            resolution={resolution}
            showSupplierCorrectionComments={showSupplierCorrectionComments}
          />
          <ProductionDeclarationMaterialAdditionalFilesList
            commodityOriginId={commodityOriginId}
            documents={documents}
            isLoading={isInitialLoading}
            materialId={currentMaterialId}
            productionDeclarationId={productionDeclarationId}
            statusCode={statusCode}
            token={token}
          />
        </div>
      </ProductionDeclarationMaterialCommodityOriginCard>
      <ProductionDeclarationMaterialAdditionalFilesDialog
        commodityOriginId={commodityOriginId}
        materialId={currentMaterialId}
        onOpenChange={setIsDialogOpen}
        open={isDialogOpen}
        productionDeclarationId={productionDeclarationId}
        token={token}
      />
    </>
  );
}
